import groupBy from 'lodash/groupBy';
import reduce from 'lodash/reduce';

/**
 * hasParentWithClassName searches class name from parent elements of given target
 * @param {Node} target - element whose parent might contain given class.
 * @param {String} className - class name string to be found
 */
export const hasParentWithClassName = (target, className) => {
  return [...document.querySelectorAll(`.${className}`)].some(
    el => el !== target && el.contains(target)
  );
};

/**
 * Listings array grouped by geolocation
 * @param {Array} mapListings - listings to be grouped on map
 * @return {Object} - Object where coordinate pair is the key to different listings
 */
export const groupedByCoordinates = mapListings => {
  return groupBy(mapListings, l => {
    const g = l.attributes.geolocation;
    return `${g.lat}-${g.lng}`;
  });
};

/**
 * Listings (in location based object literal) is mapped to array
 * @param {Object} mapListings - listings to be grouped on map
 * @return {Array} - An array where items are arrays of listings
 *   (They are arrays containing all the listings in that location)
 */
export const reducedToArray = mapListings => {
  return reduce(mapListings, (acc, listing) => acc.concat([listing]), []);
};

const EARTH_RADIUS_IN_METERS = 6371000;
const DEFAULT_DISTANCE_THRESHOLD_METERS = 1500;

const toRadians = value => (value * Math.PI) / 180;

const distanceBetween = (locationA, locationB) => {
  if (!locationA || !locationB) {
    return Infinity;
  }
  const lat1 = toRadians(locationA.lat);
  const lat2 = toRadians(locationB.lat);
  const deltaLat = toRadians(locationB.lat - locationA.lat);
  const deltaLng = toRadians(locationB.lng - locationA.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_IN_METERS * c;
};

const addListingToGroup = (group, geolocation, listing) => {
  const { listings, center } = group;
  const count = listings.length;
  const updatedCenter = {
    lat: (center.lat * count + geolocation.lat) / (count + 1),
    lng: (center.lng * count + geolocation.lng) / (count + 1),
  };
  group.center = updatedCenter;
  group.listings = [...listings, listing];
  return group;
};

/**
 * Group listings that are closer to each other than the given threshold.
 * This helps us cluster listings that sit on top of each other on the map.
 */
export const groupedByDistance = (listings, thresholdMeters = DEFAULT_DISTANCE_THRESHOLD_METERS) => {
  if (!Array.isArray(listings) || listings.length === 0) {
    return {};
  }

  if (thresholdMeters <= 0) {
    return groupedByCoordinates(listings);
  }

  const groups = [];

  listings.forEach(listing => {
    const geolocation = listing?.attributes?.geolocation;
    if (!geolocation) {
      return;
    }

    const matchingIndex = groups.findIndex(group =>
      distanceBetween(group.center, geolocation) <= thresholdMeters
    );

    if (matchingIndex >= 0) {
      groups[matchingIndex] = addListingToGroup(groups[matchingIndex], geolocation, listing);
    } else {
      groups.push({ center: { lat: geolocation.lat, lng: geolocation.lng }, listings: [listing] });
    }
  });

  return groups.reduce((acc, group, index) => {
    acc[`cluster_${index}`] = group.listings;
    return acc;
  }, {});
};
